import { noOp } from 'common/Constants';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ExpandableTextProps } from './index';

const MIN_EXPAND_TEXT_WIDTH = 100;

export const useExpandableText = (props: ExpandableTextProps) => {
  const {
    collapseText,
    expandText,
    numberOfLines,
    onClickExpand = noOp,
    onClickCollapse = noOp,
    text,
    ...otherProps
  } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedHeight, setExpandedHeight] = useState(0);
  const [expandTextWidth, setExpandTextWidth] = useState(0);
  const [lineHeight, setLineHeight] = useState(20);
  const [containerWidth, setContainerWidth] = useState(0);

  const expandedRef = useRef<HTMLDivElement>(null);
  const expandButtonRef = useRef<HTMLSpanElement>(null);
  const singleLineRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const calculateExpandButtonRef = useRef(0);

  const calculateNumberOfLines = Math.max(numberOfLines, 1);
  const calculatedCollapsedHeight = lineHeight * calculateNumberOfLines;
  const collapsedHeight = lineHeight * (calculateNumberOfLines - 1); // Height for first N-1 lines
  // Only show expand if text exceeds the number of lines (strictly greater than)
  // Use a larger buffer (2px) to account for rounding differences and ensure we only show
  // when text would actually overflow to the next line
  const showExpand = expandedHeight > calculatedCollapsedHeight + 2;

  const setMeasurements = useCallback(() => {
    if (expandedRef.current && singleLineRef.current) {
      setExpandedHeight(expandedRef.current.offsetHeight);
      setLineHeight(singleLineRef.current.offsetHeight);
    }
    if (expandButtonRef.current) {
      setExpandTextWidth(expandButtonRef.current.offsetWidth);
    }
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    setMeasurements();
  }, [setMeasurements, text]);

  useEffect(() => {
    window.addEventListener('resize', setMeasurements);
    // Calculate expand button width with interval (similar to old component)
    const intervalId = window.setInterval(() => {
      if (expandText && expandTextWidth === 0 && expandButtonRef.current) {
        setExpandTextWidth(expandButtonRef.current.offsetWidth);
      } else {
        clearInterval(intervalId);
      }
    }, 400);
    calculateExpandButtonRef.current = intervalId;
    return () => {
      window.removeEventListener('resize', setMeasurements);
      clearInterval(intervalId);
    };
  }, [expandText, expandTextWidth, setMeasurements]);

  return {
    ...otherProps,
    calculatedCollapsedHeight,
    collapsedHeight,
    collapseText,
    containerRef,
    containerWidth,
    expandedHeight,
    expandedRef,
    expandText,
    expandButtonRef,
    expandTextWidth: expandTextWidth || MIN_EXPAND_TEXT_WIDTH,
    numberOfLines: calculateNumberOfLines,
    handleCollapse: useCallback(() => {
      setIsExpanded(false);
      onClickCollapse();
    }, [onClickCollapse]),
    handleExpand: useCallback(() => {
      setIsExpanded(true);
      onClickExpand();
    }, [onClickExpand]),
    isExpanded,
    lineHeight,
    showExpand,
    singleLineRef,
    text,
  };
};
